import { api } from '@http/constants';
import type { PaginatedCollection } from '@/http/PaginatedCollection';
import fetchWrapper from '@/http/fetchWrapper';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { BoundingBox } from '@eye-able/types';

export type { PolicyViolationLabel } from './useCreatePolicy';

export type GovernanceIssueStatus = 'open' | 'fixed' | 'ignored' | 'stale';
export type GovernanceIssuePriority = 'low' | 'medium' | 'high';
export type GovernanceIssuesDateRange = '90d' | '30d' | '7d' | '24h';

export interface GovernanceIssuesFilters {
  search?: string;
  policyId?: number;
  teamId?: number;
  priority?: GovernanceIssuePriority;
  status?: GovernanceIssueStatus;
  domainIds?: number[];
  dateRange?: GovernanceIssuesDateRange;
  hasScreenshot?: boolean;
}

/**
 * Rule-evaluation result tree stored on the issue, in the same structural
 * vocabulary as the stored policy rule: group nodes carry `matchMode` plus leaf
 * conditions under `rules` and/or nested groups under `groups`; condition
 * leaves carry the tested `field`/`operator`/`value` flat on the node;
 * hand-authored rule leaves carry `id`/`description` instead. `pass` is that
 * node's outcome. For multi-match rules the root also records which
 * `occurrence` of the match on the page this issue is for.
 */
export interface RuleEvaluationNode {
  pass: boolean;
  matchMode?: 'all' | 'any';
  /** Group node: the result was inverted (`NOT`). */
  negate?: boolean;
  rules?: RuleEvaluationNode[];
  groups?: RuleEvaluationNode[];
  field?: string;
  operator?: string;
  value?: string;
  /** Leaf node fallback for hand-authored rules with no stored condition. */
  id?: string;
  description?: string;
  matchedTerms?: string[];
  occurrence?: { index: number; total: number };
}

/** A governance issue with its policy and domain foreign keys resolved. */
export interface GovernanceIssueResponse {
  id: number;
  status: GovernanceIssueStatus;
  policyId: number;
  policy: string;
  priority: GovernanceIssuePriority;
  auditEntryId: number;
  domain: string;
  /** Null when the issue is not tied to a specific page URL. */
  urls: string[] | null;
  xPath?: string | null;
  htmlElement?: string | null;
  matchedRule: RuleEvaluationNode;
  screenshotPath: string | null;
  boundingBox: BoundingBox | null;
  createdAt: string;
  statusUpdatedAt: string;
}

export type GovernanceIssuesPaginatedResponse =
  PaginatedCollection<GovernanceIssueResponse>;

/** Serialize the shared issue filters, as understood by all governanceIssues endpoints. */
export function governanceIssuesFilterParams(filters: GovernanceIssuesFilters) {
  const params = new URLSearchParams();

  const search = filters.search?.trim();
  if (search) {
    params.set('search', search);
  }

  if (filters.policyId) {
    params.set('policyId', String(filters.policyId));
  }

  if (filters.teamId) {
    params.set('teamId', String(filters.teamId));
  }

  if (filters.priority) {
    params.set('priority', filters.priority);
  }

  if (filters.status) {
    params.set('status', filters.status);
  }

  for (const domainId of filters.domainIds ?? []) {
    params.append('domainIds', String(domainId));
  }

  if (filters.dateRange) {
    params.set('dateRange', filters.dateRange);
  }

  if (filters.hasScreenshot) {
    params.set('hasScreenshot', 'true');
  }

  return params;
}

function toSearchParams({
  page,
  filters,
}: {
  page: number;
  filters: GovernanceIssuesFilters;
}) {
  const params = governanceIssuesFilterParams(filters);
  params.set('page', String(page));

  return params;
}

/** Governance issues for the current user's organisation, loaded page by page. */
export function useFetchGovernanceIssues(
  filters: GovernanceIssuesFilters = {},
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: [api.governanceIssues, filters],
    queryFn: ({ pageParam }) =>
      fetchWrapper.get<GovernanceIssuesPaginatedResponse>(
        api.governanceIssues,
        toSearchParams({ page: pageParam, filters }),
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? lastPage.currentPage + 1 : undefined,
    enabled,
    // Always refetch on mount so the issues list is fresh on arrival.
    refetchOnMount: 'always',
  });
}
